import { BankAccountStatus, EgmCardStatus, useWealthOverview } from '@egw/api'
import { GenericError, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { launchDarklyService } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import ActivateCardAttribute from '../../components/activate-card-attribute/activate-card-attribute'
import { CardNumberAttribute } from '../../components/card-number-attribute/card-number-attribute'
import CardView from '../../components/card-view/card-view'
import DisableCardAttribute from '../../components/disable-card-attribute/disable-card-attribute'
import DisabledCardSection from '../../components/disabled-card-section/disabled-card-section'
import { ReportCardAttribute } from '../../components/report-card-attribute/report-card-attribute'
import { SetPinAttribute } from '../../components/set-pin-attribute/set-pin-attribute'
import { VgsShowContextProvider } from '../../context/vsg-context-provider'

export interface DebitCardPageProps {
  alias: string
}

export interface DebitCardSectionProps {
  debitCardPresent: boolean
  cardId: string | undefined
  cardStatus: EgmCardStatus | undefined
  isCardInactive?: boolean
  alias?: number
}

export const DebitCardPage: FC<DebitCardPageProps> = ({ alias }) => {
  const { t } = useTranslation('features-account')
  const { hasFeature } = launchDarklyService

  const { data: wealthOverviewData, isLoading, error } = useWealthOverview()

  const cardDetails = wealthOverviewData?.highYieldChecking?.accounts.find(
    (account) => account.alias === parseInt(alias),
  )

  const cardStatus = cardDetails?.debitCard[0]?.cardStatus

  const showContactSupport =
    cardStatus === EgmCardStatus.SUSPENDED_BY_EGM ||
    cardStatus === EgmCardStatus.REPORTED_LOST_STOLEN ||
    cardStatus === EgmCardStatus.LOST_STOLEN

  const showAccountClosed = cardDetails?.status === BankAccountStatus.CLOSED

  const isCardInactive =
    cardStatus === EgmCardStatus.SUSPENDED_BY_EGM ||
    cardStatus === EgmCardStatus.SUSPENDED_BY_USER ||
    cardStatus === EgmCardStatus.LOST_STOLEN ||
    showAccountClosed

  const debitCardSectionProps: DebitCardSectionProps | undefined = cardDetails?.debitCard[0]
    ? {
        debitCardPresent: Boolean(cardDetails.debitCard[0]),
        cardId: cardDetails.debitCard[0]?.cardId,
        cardStatus: cardDetails.debitCard[0]?.cardStatus,
        alias: cardDetails.alias,
      }
    : undefined

  const userSuspendedCard = cardStatus === EgmCardStatus.SUSPENDED_BY_USER

  const activeCard = cardStatus === EgmCardStatus.ACTIVE
  const pendingCard = cardStatus === EgmCardStatus.PENDING_ACTIVATION
  const showCardNumberAttribute = activeCard || pendingCard

  const showDisableCardAttribute = activeCard || userSuspendedCard || pendingCard

  const showReportCardAttribute = activeCard || userSuspendedCard || pendingCard
  const showActivateButton = debitCardSectionProps?.cardId && pendingCard

  return (
    <>
      {isLoading ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <div className="egw-container">
            <Skeleton count={1} height="60px" />
          </div>
          <Skeleton count={1} height="270px" />
          <div className="egw-container">
            <Skeleton count={5} height="40px" className="mt-1" />
          </div>
        </SkeletonTheme>
      ) : null}

      {!isLoading ? (
        <PageHeader
          title={t('debit_card.title')}
          subtitle={cardDetails?.accountName}
          backLinkUrl={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD.backPath?.replace(':alias', alias.toString()) ?? ''}
        />
      ) : null}

      {cardDetails ? (
        <VgsShowContextProvider>
          {Boolean(debitCardSectionProps) && (
            <CardView
              isCardInactive={Boolean(isCardInactive)}
              cardDetails={cardDetails}
              cardDisabled={Boolean(showAccountClosed)}
            />
          )}
          <div className="egw-container pt-6 pb-6 max-md:pb-[90px] flex flex-col gap-8">
            {debitCardSectionProps && !showAccountClosed && !showContactSupport ? (
              <div>
                <h2 className="mb-2 text-xl font-light text-header">{t('debit_card.title')}</h2>
                <ul className="mt-4">
                  {showCardNumberAttribute ? <CardNumberAttribute /> : null}
                  {showDisableCardAttribute ? (
                    <DisableCardAttribute
                      cardId={debitCardSectionProps.cardId}
                      cardStatus={debitCardSectionProps.cardStatus}
                      alias={alias}
                    />
                  ) : null}
                  {activeCard ? <SetPinAttribute alias={alias} cardId={debitCardSectionProps.cardId} /> : null}
                  {showReportCardAttribute ? (
                    <ReportCardAttribute cardId={debitCardSectionProps.cardId} alias={alias} />
                  ) : null}
                  {showActivateButton ? (
                    <ActivateCardAttribute alias={Number(alias)} cardId={debitCardSectionProps.cardId ?? ''} />
                  ) : null}
                </ul>
              </div>
            ) : null}
            {showContactSupport ? <DisabledCardSection /> : null}
          </div>
        </VgsShowContextProvider>
      ) : null}

      {Boolean(error) && (
        <div className="egw-container">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      )}
    </>
  )
}
